/**
 * Unit Toggle - MM/Inches Conversion
 * Handles unit conversion for product dimensions using localStorage for persistence.
 *
 * Usage:
 * - Elements with [data-unit-convert] attribute will have their measurements converted
 * - Measurements in mm: "600mm", "100x200mm", "50-100mm"
 * - Measurements in m: "3.5m", "2m", "2-3.5m"
 * - User preference stored in localStorage (persists indefinitely)
 */

const UNIT_STORAGE_KEY = 'dd-unit-preference';
const UNIT_RATIO = 25.4;
const METRIC_UNIT = 'mm';
const IMPERIAL_UNIT = 'inches';

/**
 * Measurement pattern for millimeters:
 * - Single values: 600mm
 * - Dimensions with x: 100x200mm, 100x200x50mm
 * - Ranges with -: 50-100mm
 */
const MM_PATTERN = /((?:\d+(?:x)\d+(?:x)\d+mm)|(?:(?:\d+(?:x|-)|)\d+mm))(?=[^-"]|$)/gi;

/**
 * Measurement pattern for meters:
 * - Single values: 3.5m, 2m
 * - Ranges with -: 2-3.5m
 * - Negative lookahead prevents matching 'mm' or other words ending in 'm'
 */
const M_PATTERN = /(\d+\.?\d*(?:-\d+\.?\d*)?m)(?!m)(?![a-z])/gi;

if (!customElements.get('unit-toggle')) {
  customElements.define(
    'unit-toggle',
    class UnitToggle extends HTMLElement {
      constructor() {
        super();
        this.buttons = null;
        this.convertibleElements = [];
        this.currentUnit = METRIC_UNIT;
      }

      connectedCallback() {
        this.buttons = this.querySelectorAll('.unit-toggle__button');
        if (!this.buttons.length) return;

        this.init();
      }

      /**
       * Initializes the unit toggle component
       */
      init() {
        this.collectConvertibleElements();
        this.processElements();
        this.loadUserPreference();
        this.bindEvents();
      }

      /**
       * Collects all elements with [data-unit-convert] attribute on the page
       */
      collectConvertibleElements() {
        this.convertibleElements = document.querySelectorAll('[data-unit-convert]');
      }

      /**
       * Processes elements to wrap measurements in spans with data attributes
       * Handles both mm and m measurements
       */
      processElements() {
        this.convertibleElements.forEach((element) => {
          if (element.dataset.unitProcessed) return;

          let processedHTML = element.innerHTML;

          processedHTML = processedHTML.replace(MM_PATTERN, (match) => {
            const metricValue = match;
            const imperialValue = this.convertToImperial(match);
            return `<span class="unit-value" data-metric="${metricValue}" data-imperial="${imperialValue}">${metricValue}</span>`;
          });

          processedHTML = processedHTML.replace(M_PATTERN, (match) => {
            const metricValue = match;
            const imperialValue = this.convertToImperial(match);
            return `<span class="unit-value" data-metric="${metricValue}" data-imperial="${imperialValue}">${metricValue}</span>`;
          });

          element.innerHTML = processedHTML;
          element.dataset.unitProcessed = 'true';
        });
      }

      /**
       * Converts a metric measurement string to imperial
       * Handles both mm and m units
       * Stores value WITHOUT inch symbol to avoid HTML attribute issues
       * The inch symbol is added during display in applyUnit()
       * @param {string} metricStr - Measurement string like "600mm", "100x200mm", or "3.5m"
       * @returns {string} Imperial measurement value (without inch symbol)
       */
      convertToImperial(metricStr) {
        const isMeters = metricStr.endsWith('m') && !metricStr.endsWith('mm');
        const cleanValue = isMeters ? metricStr.replace(/m$/, '') : metricStr.replace('mm', '');
        const conversionFn = isMeters
          ? (val) => this.metersToInches(parseFloat(val))
          : (val) => this.mmToInches(parseFloat(val));

        if (cleanValue.includes('x')) {
          const parts = cleanValue.split('x');
          const converted = parts.map(conversionFn);
          return converted.join('x');
        }

        if (cleanValue.includes('-')) {
          const parts = cleanValue.split('-');
          const converted = parts.map(conversionFn);
          return converted.join('-');
        }

        return conversionFn(cleanValue);
      }

      /**
       * Converts meters to inches
       * @param {number} m - Value in meters
       * @returns {string} Value in inches, rounded to 2 decimal places
       */
      metersToInches(m) {
        return ((m * 1000) / UNIT_RATIO).toFixed(2);
      }

      /**
       * Converts millimeters to inches
       * @param {number} mm - Value in millimeters
       * @returns {string} Value in inches, rounded to 2 decimal places
       */
      mmToInches(mm) {
        return (mm / UNIT_RATIO).toFixed(2);
      }

      /**
       * Loads user preference from localStorage and sets initial state
       */
      loadUserPreference() {
        const savedUnit = localStorage.getItem(UNIT_STORAGE_KEY);

        if (savedUnit === IMPERIAL_UNIT) {
          this.currentUnit = IMPERIAL_UNIT;
          this.setActiveButton(IMPERIAL_UNIT);
          this.applyUnit(IMPERIAL_UNIT);
        } else {
          this.currentUnit = METRIC_UNIT;
          this.setActiveButton(METRIC_UNIT);
          this.applyUnit(METRIC_UNIT);
        }
      }

      /**
       * Sets the active state on the correct button
       * @param {string} unit - The unit to activate ('mm' or 'inches')
       */
      setActiveButton(unit) {
        this.buttons.forEach((button) => {
          const buttonUnit = button.dataset.unit;
          const isActive = buttonUnit === unit;

          button.classList.toggle('unit-toggle__button--active', isActive);
          button.setAttribute('aria-checked', isActive ? 'true' : 'false');
        });
      }

      /**
       * Binds event listeners
       */
      bindEvents() {
        this.buttons.forEach((button) => {
          button.addEventListener('click', this.handleButtonClick.bind(this));
        });
      }

      /**
       * Handles button click event
       * @param {Event} event - The click event
       */
      handleButtonClick(event) {
        const button = event.currentTarget;
        const unit = button.dataset.unit;

        if (unit === this.currentUnit) return;

        this.currentUnit = unit;
        localStorage.setItem(UNIT_STORAGE_KEY, unit);
        this.setActiveButton(unit);
        this.applyUnit(unit);
        this.dispatchUnitChangeEvent();
      }

      /**
       * Applies the selected unit to all convertible elements
       * @param {string} unit - The unit to apply ('mm' or 'inches')
       */
      applyUnit(unit) {
        const unitValues = document.querySelectorAll('.unit-value');

        unitValues.forEach((span) => {
          const metric = span.dataset.metric;
          const imperial = span.dataset.imperial;

          if (unit === IMPERIAL_UNIT) {
            span.innerHTML = `<abbr title="${metric}">${imperial}"</abbr>`;
          } else {
            span.textContent = metric;
          }
        });
      }

      /**
       * Dispatches a custom event when unit changes
       */
      dispatchUnitChangeEvent() {
        const event = new CustomEvent('unit:changed', {
          bubbles: true,
          detail: {
            unit: this.currentUnit,
            isImperial: this.currentUnit === IMPERIAL_UNIT,
          },
        });
        this.dispatchEvent(event);
      }

      /**
       * Gets the current unit preference
       * @returns {string} Current unit ('mm' or 'inches')
       */
      getCurrentUnit() {
        return this.currentUnit;
      }

      /**
       * Checks if imperial unit is currently selected
       * @returns {boolean} True if imperial, false if metric
       */
      isImperial() {
        return this.currentUnit === IMPERIAL_UNIT;
      }
    }
  );
}

/**
 * Utility function to get current unit preference from localStorage
 * Can be used by other scripts that need to know the current unit
 * @returns {string} 'mm' or 'inches'
 */
window.getUnitPreference = function () {
  return localStorage.getItem(UNIT_STORAGE_KEY) || 'mm';
};

/**
 * Checks if the current unit preference is imperial (inches)
 * @returns {boolean} True if imperial, false if metric
 */
window.isImperialUnit = function () {
  return localStorage.getItem(UNIT_STORAGE_KEY) === IMPERIAL_UNIT;
};

/**
 * Utility function to convert mm to inches
 * @param {number} mm - Value in millimeters
 * @returns {number} Value in inches
 */
window.mmToInches = function (mm) {
  return mm / UNIT_RATIO;
};

/**
 * Utility function to convert inches to mm
 * @param {number} inches - Value in inches
 * @returns {number} Value in millimeters
 */
window.inchesToMm = function (inches) {
  return inches * UNIT_RATIO;
};

/**
 * Utility function to convert meters to inches
 * @param {number} m - Value in meters
 * @returns {number} Value in inches
 */
window.metersToInches = function (m) {
  return (m * 1000) / UNIT_RATIO;
};

/**
 * Formats a mm value according to current unit preference
 * @param {number} mm - Value in millimeters
 * @returns {string} Formatted value with unit (e.g., "600mm" or "23.62"")
 */
window.formatMeasurement = function (mm) {
  if (window.isImperialUnit()) {
    return (mm / UNIT_RATIO).toFixed(2) + '"';
  }
  return mm + 'mm';
};

/**
 * Gets the current unit symbol
 * @returns {string} 'mm' or '"'
 */
window.getUnitSymbol = function () {
  return window.isImperialUnit() ? '"' : 'mm';
};

/**
 * Formats a range of mm values according to current unit preference
 * @param {number} min - Minimum value in mm
 * @param {number} max - Maximum value in mm
 * @returns {string} Formatted range (e.g., "600 - 1050 mm" or "23.62 - 41.34"")
 */
window.formatMeasurementRange = function (min, max) {
  if (window.isImperialUnit()) {
    return (min / UNIT_RATIO).toFixed(2) + '-' + (max / UNIT_RATIO).toFixed(2) + '"';
  }
  return min + ' - ' + max + ' mm';
};
